import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useWorkspace } from "./useWorkspace";
import { toast } from "sonner";

export type FontChoice = "sans" | "serif";
export type WorkspaceFontChoice = FontChoice | null; // null = inherit account default

const QK_USER_PREFS = ["user_preferences"] as const;

/**
 * Resolves the effective reading font for the current user + active workspace.
 *
 * Resolution order:
 *   workspace.workspace_font_preference  (per-workspace override)
 *   ?? user_preferences.font_preference  (account default)
 *   ?? "sans"                            (hard default)
 *
 * The `effective` value is what the UI should use. Mutators persist + invalidate.
 */
export function useFontPreference() {
  const { user } = useAuth();
  const { active, refresh } = useWorkspace();
  const qc = useQueryClient();

  // Account-level preference
  const userQuery = useQuery({
    queryKey: QK_USER_PREFS,
    enabled: !!user,
    queryFn: async (): Promise<FontChoice> => {
      const { data, error } = await supabase
        .from("user_preferences")
        .select("font_preference")
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      return ((data?.font_preference as FontChoice) ?? "sans");
    },
  });

  const userPref: FontChoice = userQuery.data ?? "sans";
  // `workspace_font_preference` was added to profiles in migration 2026-05-02.
  // Workspaces are loaded from the `profiles` table via useWorkspace.
  const workspacePref =
    ((active as unknown as { workspace_font_preference?: WorkspaceFontChoice })
      ?.workspace_font_preference ?? null) as WorkspaceFontChoice;

  const effective: FontChoice = workspacePref ?? userPref;

  const setUserPreference = useMutation({
    mutationFn: async (next: FontChoice) => {
      if (!user) throw new Error("Not signed in");
      const { error } = await supabase
        .from("user_preferences")
        .upsert(
          { user_id: user.id, font_preference: next },
          { onConflict: "user_id" },
        );
      if (error) throw error;
      return next;
    },
    onSuccess: (next) => {
      qc.setQueryData(QK_USER_PREFS, next);
    },
    onError: (err) => {
      toast.error("Could not save reading font", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    },
  });

  const setWorkspaceOverride = useMutation({
    mutationFn: async (next: WorkspaceFontChoice) => {
      if (!active) throw new Error("No active workspace");
      const { error } = await supabase
        .from("profiles")
        .update({ workspace_font_preference: next })
        .eq("id", active.id);
      if (error) throw error;
      return next;
    },
    onSuccess: async () => {
      await refresh();
    },
    onError: (err) => {
      toast.error("Could not save workspace font", {
        description: err instanceof Error ? err.message : "Please try again.",
      });
    },
  });

  return {
    userPref,
    workspacePref,
    effective,
    isLoading: userQuery.isLoading,
    setUserPreference: setUserPreference.mutateAsync,
    setWorkspaceOverride: setWorkspaceOverride.mutateAsync,
    isSavingUser: setUserPreference.isPending,
    isSavingWorkspace: setWorkspaceOverride.isPending,
  };
}

/**
 * Mounts the body class that switches the global font family. Call once
 * inside AppShell so the resolved choice applies app-wide.
 */
export function useApplyFontPreferenceToBody() {
  const { effective, isLoading } = useFontPreference();
  const [applied, setApplied] = useState<FontChoice | null>(null);

  const apply = useCallback((choice: FontChoice) => {
    const body = document.body;
    body.classList.remove("font-pref-sans", "font-pref-serif");
    body.classList.add(`font-pref-${choice}`);
    setApplied(choice);
  }, []);

  useEffect(() => {
    if (isLoading && !applied) return;
    apply(effective);
  }, [effective, isLoading, applied, apply]);

  return effective;
}
